<template>
	<layout-admin>
		<!-- Kopfzeile -->
		<header class="bg-white shadow">
			<div class="py-6 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
				<h1 class="text-3xl font-bold leading-tight text-gray-900">
					{{ __("Anfragen") }}
				</h1>
			</div>
		</header>

		<admin-table
			:rows="rows"
			:cols="cols"
			:loading="loading"
			:sort-columns="sort"
			:filter-values="filters"
			:pagination="pagination"
			@sort-change="handleSortChange"
			@filter-change="handleFilterChange"
			@page-change="loadContent"
		>
			<template v-slot:cell-status="{ row }">
				<span
					class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
					:class="row.status === 'DONE' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'"
				>
					{{ statusLabel(row.status) }}
				</span>
			</template>

			<template v-slot:row-actions="{ row }">
				<ui-button
					type="secondary"
					:disabled="updatingId === row.id"
					@click="updateStatus(row)"
				>
					{{ row.status === "NEW" ? __("Als erledigt markieren") : __("Wieder öffnen") }}
				</ui-button>
			</template>
		</admin-table>
	</layout-admin>
</template>

<script>
import axios from "axios";
import { formatDate, formatMonth } from "../../utils/date";
import { formatLocalizedNumber } from "../../cropOptions";

export default {
	name: "LocalPriceInquiriesGrid",
	props: {
		statusOptions: { type: Array, required: true },
		locale: { type: String, required: true },
	},

	data() {
		return {
			loading: true,
			rows: [],
			pagination: { currentPage: 1, lastPage: 1, total: 0 },
			sort: [],
			filters: {},
			updatingId: null,
		};
	},

	computed: {
		cols() {
			return [
				{
					id: "created_at",
					name: this.__("Datum"),
					render: (v) => formatDate(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "contact_name",
					name: this.__("Name"),
					sortable: false,
					render: (v) => v || "—",
				},
				{
					id: "company_name",
					name: this.__("Firma"),
					sortable: false,
					render: (v) => v || "—",
				},
				{
					id: "street",
					name: this.__("Adresse"),
					sortable: false,
					render: (v, row) => {
						const street = row.street_geocoded ? row.street : null;
						const place  = [row.zip, row.city].filter(Boolean).join(" ");
						const parts  = [street, place].filter(Boolean);

						return parts.length ? parts.join(", ") : "—";
					},
				},
				{
					id: "product__grade",
					name: this.__("Produkt"),
					sortable: false,
					render: (v) => {
						if (!v) {
							return "—";
						}

						const [grade, protein] = v.split("__");
						const label = this.__("product.grades.default." + grade);

						return protein ? `${label} (${this.__("product.proteins." + protein)})` : label;
					},
				},
				{
					id: "product_harvest",
					name: this.__("Erntejahr"),
					render: (v) => v ?? "—",
				},
				{
					id: "date_month_from",
					name: this.__("Von"),
					sortable: false,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "date_month_to",
					name: this.__("Bis"),
					sortable: false,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "market_location_city",
					name: this.__("Marktstandort"),
					sortable: false,
					render: (v, row) => {
						if (!row.market_location_city && !row.market_location_zip) {
							return "—";
						}

						const place = row.market_location_city
							? `${row.market_location_city} (${row.market_location_zip})`
							: row.market_location_zip;

						return row.market_location_name ? `${row.market_location_name}, ${place}` : place;
					},
				},
				{
					id: "price_source_name",
					name: this.__("Preisquelle"),
					sortable: false,
					render: (v) => v || "—",
				},
				{
					id: "price_mt",
					name: this.__("Bestpreis"),
					render: (v) => (v != null ? `${formatLocalizedNumber(v)} €/t` : "—"),
				},
				{
					id: "qty_mt",
					name: this.__("Menge"),
					render: (v) => (v != null ? `${formatLocalizedNumber(v)} t` : "—"),
				},
				{
					id: "contact_method",
					name: this.__("Kontaktweg"),
					sortable: false,
					render: (v) => {
						const labels = { PHONE: this.__("Telefon"), EMAIL: this.__("E-Mail") };

						return labels[v] || "—";
					},
				},
				{
					id: "contact_phone",
					name: this.__("Kontakt"),
					sortable: false,
					render: (v, row) => row.contact_phone || row.contact_email || "—",
				},
				{
					id: "status",
					name: this.__("Status"),
					sortable: true,
					render: (v) => this.statusLabel(v),
					filterOptions: this.statusOptions,
				},
			];
		},
	},

	mounted() {
		this.loadContent(1);
	},

	methods: {
		/**
		 * Lädt eine Seite der Bekundungen (Sortierung/Filter aus dem lokalen State).
		 *
		 * @param {number} page
		 */
		loadContent(page = 1) {
			this.loading = true;

			axios
				.get(this.$route("admin.local_price_inquiries.data"), {
					params: {
						page,
						per_page: 50,
						// TODO: Workaround für Axios 0.21.x — mit Axios 1.x direkt als Objekt senden
						sort: JSON.stringify(this.sort),
						filters: JSON.stringify(this.filters),
					},
				})
				.then((response) => {
					this.rows = response.data.data;
					this.pagination.currentPage = response.data.current_page;
					this.pagination.lastPage = response.data.last_page;
					this.pagination.total = response.data.total;
				})
				.catch(() => {
					this.$bus.$emit("toast", {
						type: "error",
						message: this.__("Die Daten konnten nicht geladen werden."),
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},

		/**
		 * Multi-Sort umschalten: none → asc → desc → none, dann neu laden.
		 *
		 * @param {string} colId
		 */
		handleSortChange(colId) {
			const idx = this.sort.findIndex((s) => s.column === colId);

			if (idx === -1) {
				this.sort.push({ column: colId, dir: "asc" });
			} else if (this.sort[idx].dir === "asc") {
				this.sort.splice(idx, 1, { column: colId, dir: "desc" });
			} else {
				this.sort.splice(idx, 1);
			}

			this.loadContent(1);
		},

		/**
		 * Filter setzen/zurücksetzen und neu laden.
		 *
		 * @param {{ colId: string, value: * }} payload
		 */
		handleFilterChange({ colId, value }) {
			if (value === null) {
				this.$delete(this.filters, colId);
			} else {
				this.$set(this.filters, colId, value);
			}

			this.loadContent(1);
		},

		/**
		 * Status einer Bekundung umschalten (NEW ↔ DONE).
		 *
		 * @param {Object} row
		 */
		updateStatus(row) {
			const newStatus = row.status === "NEW" ? "DONE" : "NEW";
			this.updatingId = row.id;

			axios
				.patch(this.$route("admin.local_price_inquiries.status", { id: row.id }), {
					status: newStatus,
				})
				.then((response) => {
					row.status = response.data.status;
					this.$bus.$emit("toast", {
						type: "success",
						message: this.__("Status aktualisiert."),
					});
				})
				.catch(() => {
					this.$bus.$emit("toast", {
						type: "error",
						message: this.__("Status konnte nicht geändert werden."),
					});
				})
				.finally(() => {
					this.updatingId = null;
				});
		},

		/**
		 * Deutsches Label zu einem Status-Code aus statusOptions.
		 *
		 * @param {string} status
		 * @returns {string}
		 */
		statusLabel(status) {
			const option = this.statusOptions.find((o) => o.id === status);

			return option ? option.name : status;
		},
	},
};
</script>
